import { getTransparentHex } from '~common/utils/getTransparentHex'
import type { Theme } from './types'

export const lightTheme: Theme = {
    colors: {
        greyScale: {
            // Black with opacity: 100%, 80%, 40%, 20%, 10%, 4%
            0: '#000000', // Black 100%
            50: '#000000CC', // Black 80%
            100: '#00000066', // Black 40%
            200: '#00000033', // Black 20%
            300: '#0000001A', // Black 10%
            400: '#0000000A', // Black 4%
            450: '#00000005', // Black 2%
            // White with opacity: 4%, 10%, 20%, 40%, 80%, 100%
            500: '#FFFFFF0A', // White 4%
            600: '#FFFFFF1A', // White 10%
            700: '#FFFFFF33', // White 20%
            800: '#FFFFFF66', // White 40%
            900: '#FFFFFFCC', // White 80%
            1000: '#FFFFFF', // White 100%
        },
        success: '#94e9b8',
        warning: '#ffdb56',
        error: '#ff4747',
        info: '#92bfff',
        background: {
            bg1: '#ffffff',
            bg2: '#f9f9fa',
            bg3: '#e6f1fd',
            bg4: '#edeefc',
            bg5: '#fafbfc',
            bg6: '#F7F7F7',
        },
        text: {
            primary: '#000000', // Black 100%
            secondary: '#00000066', // Black 40%
            tertiary: '#80868b',
            inverse: '#ffffff',
        },
        border: {
            primary: '#e8eaed',
            secondary: '#dadce0',
            tertiary: '#e8eaed',
            focus: '#5f6368',
        },
    },
    spacing: {
        xs: '4px', // 0.25rem
        sm: '8px', // 0.5rem
        md: '16px', // 1rem
        lg: '24px', // 1.5rem
        xl: '32px', // 2rem
        xxl: '48px', // 3rem
        0.5: '2px', // 0.125rem
        0: '0',
        1: '4px', // 0.25rem
        1.5: '6px', // 0.375rem
        2: '8px', // 0.5rem
        3: '12px', // 0.75rem
        4: '16px', // 1rem
        5: '20px', // 1.25rem
        6: '24px', // 1.5rem
        8: '32px', // 2rem
        10: '40px', // 2.5rem
        12: '48px', // 3rem
        16: '64px', // 4rem
        20: '80px', // 5rem
        24: '96px', // 6rem
        32: '128px', // 8rem
        40: '160px', // 10rem
        48: '192px', // 12rem
        56: '224px', // 14rem
        64: '256px', // 16rem
    },
    typography: {
        fontFamily: {
            sans: '"Nunito", system-ui, -apple-system, Segoe UI, Roboto, Helvetica, Arial, "Apple Color Emoji", "Segoe UI Emoji", "Segoe UI Symbol", sans-serif',
            // sans: '"Work Sans", system-ui, -apple-system, Segoe UI, Roboto, Helvetica, Arial, "Apple Color Emoji", "Segoe UI Emoji", "Segoe UI Symbol", sans-serif',
            serif: 'ui-serif, Georgia, Cambria, "Times New Roman", Times, serif',
            mono: 'ui-monospace, SFMono-Regular, Menlo, Monaco, Consolas, "Liberation Mono", "Courier New", monospace',
        },
        fontSize: {
            xs: '12px', // 0.75rem
            sm: '14px', // 0.875rem
            base: '16px', // 1rem
            lg: '18px', // 1.125rem
            xl: '20px', // 1.25rem
            '2xl': '24px', // 1.5rem
            '3xl': '30px', // 1.875rem
            '4xl': '36px', // 2.25rem
            '5xl': '48px', // 3rem
            '6xl': '60px', // 3.75rem
        },
        fontWeight: {
            light: 300,
            normal: 400,
            medium: 500,
            semibold: 600,
            bold: 700,
            extrabold: 800,
            black: 900,
        },
        lineHeight: {
            none: 1,
            tight: 1.25,
            snug: 1.375,
            normal: 1.5,
            relaxed: 1.625,
            loose: 2,
        },
    },
    breakpoints: {
        xs: '0px',
        sm: '640px',
        md: '768px',
        lg: '1024px',
        xl: '1280px',
        '2xl': '1536px',
    },
    shadows: {
        sm: '0 1px 2px 0 rgb(0 0 0 / 0.05)',
        md: '0 4px 6px -1px rgb(0 0 0 / 0.1), 0 2px 4px -2px rgb(0 0 0 / 0.1)',
        lg: '0 10px 15px -3px rgb(0 0 0 / 0.1), 0 4px 6px -4px rgb(0 0 0 / 0.1)',
        xl: '0 20px 25px -5px rgb(0 0 0 / 0.1), 0 8px 10px -6px rgb(0 0 0 / 0.1)',
        '2xl': '0 25px 50px -12px rgb(0 0 0 / 0.25)',
        inner: 'inset 0 2px 4px 0 rgb(0 0 0 / 0.05)',
        none: '0 0 #0000',
    },
    borderRadius: {
        none: '0',
        sm: '2px', // 0.125rem
        md: '6px', // 0.375rem
        lg: '8px', // 0.5rem
        xl: '12px', // 0.75rem
        '2xl': '16px', // 1rem
        full: '9999px',
    },
    transitions: {
        fast: '150ms ease-in-out',
        normal: '200ms ease-in-out',
        slow: '300ms ease-in-out',
    },
}

export const darkTheme: Theme = {
    ...lightTheme,
    colors: {
        greyScale: {
            // For dark theme, we invert the pattern:
            // White with opacity: 100%, 80%, 40%, 20%, 10%, 4%
            0: '#FFFFFF', // White 100%
            50: '#FFFFFFCC', // White 80%
            100: '#FFFFFF66', // White 40%
            200: '#FFFFFF33', // White 20%
            300: '#FFFFFF1A', // White 10%
            400: '#FFFFFF0A', // White 4%
            450: '#00000005', // Black 2%
            // Black with opacity: 4%, 10%, 20%, 40%, 80%, 100%
            500: '#FFFFFF0A', // Black 4%
            600: '#FFFFFF1A', // Black 10%
            700: '#33000000', // Black 20%
            800: '#FFFFFF66', // Black 40%
            900: '#FFFFFFCC', // Black 80%
            1000: '#000000', // Black 100%
        },
        success: '#94e9b8',
        warning: '#ffdb56',
        error: '#ff4747',
        info: '#92bfff',
        background: {
            bg1: '#2a2a2a',
            bg2: getTransparentHex('#FFFFFF', 4),
            bg3: getTransparentHex('#000000', 10),
            bg4: '#EDEEFC', // Light Blue 100%
            bg5: '#bdc1c6',
            bg6: getTransparentHex('#000000', 4),
        },
        text: {
            primary: '#ffffff',
            secondary: getTransparentHex('#ffffff', 40),
            tertiary: '#dadce0',
            inverse: '#202124',
        },
        border: {
            primary: '#5f6368',
            secondary: '#80868b',
            tertiary: '#5f6368',
            focus: '#bdc1c6',
        },
    },
}
